import React, { useState, useEffect } from "react";
import {
  Plus,
  Printer,
  MoreVertical,
  Search,
  Edit2,
  Trash2,
  RefreshCw,
  X,
  Save,
  Wifi,
  Usb,
  Monitor,
  Receipt,
  UtensilsCrossed,
  CheckCircle2,
  Star,
  TestTube2,
  Loader2,
  ChefHat,
  LayoutGrid,
  List,
  Settings,
  Power,
  Circle,
  ScanSearch,
  Download,
  Server,
  Laptop,
  AlertCircle,
  Info,
  Zap,
  Cloud,
  HardDrive,
  CheckCircle,
  XCircle,
} from "lucide-react";
import usePrinters from "../../../hooks/usePrinters";
import printService from "../../../services/printService";

// Printer Type Badge Colors
const PRINTER_TYPE_COLORS = {
  BILL: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200", icon: Receipt },
  KOT: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", icon: UtensilsCrossed },
};

// Connection Type Icons
const CONNECTION_ICONS = {
  WINDOWS: Monitor,
  NETWORK: Wifi,
  USB: Usb,
};

export default function PrinterConfigurations({ renderActions }) {
  // Tab state: "local" or "server"
  const [activeTab, setActiveTab] = useState("local");

  // Common states
  const [filterText, setFilterText] = useState("");
  const [filterType, setFilterType] = useState("ALL");
  const [viewMode, setViewMode] = useState("card");
  const [testingPrinter, setTestingPrinter] = useState(null);

  // Server printer states
  const [showServerForm, setShowServerForm] = useState(false);
  const [editServerPrinter, setEditServerPrinter] = useState(null);

  // Local printer states
  const [showLocalForm, setShowLocalForm] = useState(false);
  const [editLocalPrinter, setEditLocalPrinter] = useState(null);

  const {
    // Server printers
    printers,
    kitchenStations,
    systemPrinters,
    loading,
    loadingSystemPrinters,
    fetchSystemPrinters,
    createPrinter,
    updatePrinter,
    deletePrinter,
    activatePrinter,
    deactivatePrinter,
    setDefaultPrinter,
    testPrint,
    refreshData,
    // Print Agent
    printAgentStatus,
    printAgentPrinters,
    checkPrintAgent,
    savePrintAgentPrinter,
    deletePrintAgentPrinter,
    testPrintAgentPrinter,
    discoverPrinters,
    getWindowsPrinters,
  } = usePrinters();

  // Handle server printer save
  const handleServerSave = async (data) => {
    try {
      if (editServerPrinter) {
        await updatePrinter(editServerPrinter.id, data);
      } else {
        await createPrinter(data);
      }
      setShowServerForm(false);
      setEditServerPrinter(null);
    } catch (error) {
      console.error("Failed to save printer:", error);
    }
  };

  // Handle local printer save
  const handleLocalSave = async (data) => {
    try {
      await savePrintAgentPrinter(data);
      setShowLocalForm(false);
      setEditLocalPrinter(null);
    } catch (error) {
      console.error("Failed to save local printer:", error);
    }
  };

  const handleTestPrint = async (printerType) => {
    setTestingPrinter(printerType);
    try {
      await testPrint(printerType);
    } finally {
      setTestingPrinter(null);
    }
  };

  // Filter server printers
  const filteredServerPrinters = printers.filter((printer) => {
    const text = filterText.toLowerCase();
    const matchesText =
      printer.name?.toLowerCase().includes(text) ||
      printer.printerName?.toLowerCase().includes(text) ||
      printer.ipAddress?.toLowerCase().includes(text) ||
      printer.kitchenStationName?.toLowerCase().includes(text);
    const matchesType = filterType === "ALL" || printer.printerType === filterType;
    return matchesText && matchesType;
  });

  // Filter local printers
  const filteredLocalPrinters = printAgentPrinters.filter((printer) => {
    const text = filterText.toLowerCase();
    const matchesText =
      printer.name?.toLowerCase().includes(text) ||
      printer.address?.toLowerCase().includes(text) ||
      printer.stationName?.toLowerCase().includes(text);
    const matchesType = filterType === "ALL" || printer.type === filterType;
    return matchesText && matchesType;
  });

  const getPrinterTypeConfig = (type) => {
    return PRINTER_TYPE_COLORS[type] || PRINTER_TYPE_COLORS.BILL;
  };

  const getConnectionIcon = (connectionType) => {
    return CONNECTION_ICONS[connectionType] || Monitor;
  };

  // Call renderActions if provided - use useCallback to memoize handlers
  React.useEffect(() => {
    if (renderActions) {
      renderActions({
        onAdd: () => {
          if (activeTab === "local") {
            setEditLocalPrinter(null);
            setShowLocalForm(true);
          } else {
            setEditServerPrinter(null);
            setShowServerForm(true);
          }
        },
        onRefresh: () => {
          refreshData();
          checkPrintAgent();
        },
        onTestBill: () => handleTestPrint("BILL"),
        onTestKOT: () => handleTestPrint("KOT"),
        testingPrinter,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testingPrinter, activeTab]);

  return (
    <div className="p-4">
      <div className="w-full">
        {/* Tab Header */}
        <div className="mb-6">
          {/* Tab Navigation - Clean underline style */}
          <div className="border-b border-slate-200">
            <div className="flex gap-0">
              {/* Local Printers Tab */}
              <button
                onClick={() => setActiveTab("local")}
                className={`relative flex items-center gap-2 px-5 py-3 text-sm font-medium transition-all border-b-2 -mb-px ${
                  activeTab === "local"
                    ? "border-teal-600 text-teal-700"
                    : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
                }`}
              >
                <Laptop size={18} />
                <span>Local Printers</span>
                {/* Status Badge */}
                <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                  printAgentStatus.available
                    ? "bg-teal-100 text-teal-700"
                    : "bg-amber-100 text-amber-700"
                }`}>
                  <Circle size={6} fill="currentColor" className={printAgentStatus.available ? "animate-pulse" : ""} />
                  {printAgentStatus.available ? "Connected" : "Offline"}
                </span>
                {/* Printer Count */}
                {printAgentPrinters.length > 0 && (
                  <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs font-semibold">
                    {printAgentPrinters.length}
                  </span>
                )}
              </button>

              {/* Server Printers Tab */}
              <button
                onClick={() => setActiveTab("server")}
                className={`relative flex items-center gap-2 px-5 py-3 text-sm font-medium transition-all border-b-2 -mb-px ${
                  activeTab === "server"
                    ? "border-teal-600 text-teal-700"
                    : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
                }`}
              >
                <Server size={18} />
                <span>Server Printers</span>
                {/* Printer Count */}
                {printers.length > 0 && (
                  <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs font-semibold">
                    {printers.length}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Tab Description */}
          <div className="mt-3 px-1">
            {activeTab === "local" ? (
              <p className="text-xs text-slate-500 flex items-center gap-2">
                <Info size={14} className="text-teal-600" />
                Local printers are configured on this computer via Print Agent.
              </p>
            ) : (
              <p className="text-xs text-slate-500 flex items-center gap-2">
                <Info size={14} className="text-teal-600" />
                Server printers are stored in the database. Only works for on-premise deployments with printers connected to server.
              </p>
            )}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === "local" ? (
          <LocalPrintersTab
            status={printAgentStatus}
            printers={filteredLocalPrinters}
            allPrinters={printAgentPrinters}
            kitchenStations={kitchenStations}
            filterText={filterText}
            setFilterText={setFilterText}
            filterType={filterType}
            setFilterType={setFilterType}
            viewMode={viewMode}
            setViewMode={setViewMode}
            onRefresh={checkPrintAgent}
            onAdd={() => {
              setEditLocalPrinter(null);
              setShowLocalForm(true);
            }}
            onEdit={(printer) => {
              setEditLocalPrinter(printer);
              setShowLocalForm(true);
            }}
            onDelete={deletePrintAgentPrinter}
            onTest={testPrintAgentPrinter}
            getPrinterTypeConfig={getPrinterTypeConfig}
            getWindowsPrinters={getWindowsPrinters}
          />
        ) : (
          <ServerPrintersTab
            printers={filteredServerPrinters}
            allPrinters={printers}
            kitchenStations={kitchenStations}
            loading={loading}
            filterText={filterText}
            setFilterText={setFilterText}
            filterType={filterType}
            setFilterType={setFilterType}
            viewMode={viewMode}
            setViewMode={setViewMode}
            onRefresh={refreshData}
            onAdd={() => {
              setEditServerPrinter(null);
              setShowServerForm(true);
            }}
            onEdit={(printer) => {
              setEditServerPrinter(printer);
              setShowServerForm(true);
            }}
            onDelete={deletePrinter}
            onActivate={activatePrinter}
            onDeactivate={deactivatePrinter}
            onSetDefault={setDefaultPrinter}
            getPrinterTypeConfig={getPrinterTypeConfig}
            getConnectionIcon={getConnectionIcon}
          />
        )}
      </div>

      {/* Local Printer Form Modal */}
      {showLocalForm && (
        <LocalPrinterForm
          item={editLocalPrinter}
          kitchenStations={kitchenStations}
          onClose={() => {
            setShowLocalForm(false);
            setEditLocalPrinter(null);
          }}
          onSave={handleLocalSave}
          onDiscover={discoverPrinters}
          onGetWindowsPrinters={getWindowsPrinters}
        />
      )}

      {/* Server Printer Form Modal */}
      {showServerForm && (
        <ServerPrinterForm
          item={editServerPrinter}
          kitchenStations={kitchenStations}
          systemPrinters={systemPrinters}
          loadingSystemPrinters={loadingSystemPrinters}
          onFetchSystemPrinters={fetchSystemPrinters}
          onClose={() => {
            setShowServerForm(false);
            setEditServerPrinter(null);
          }}
          onSave={handleServerSave}
        />
      )}
    </div>
  );
}

// ============================================================
// Local Printers Tab Component
// ============================================================
function LocalPrintersTab({
  status,
  printers,
  allPrinters,
  kitchenStations,
  filterText,
  setFilterText,
  filterType,
  setFilterType,
  viewMode,
  setViewMode,
  onRefresh,
  onAdd,
  onEdit,
  onDelete,
  onTest,
  getPrinterTypeConfig,
  getWindowsPrinters,
}) {
  // Download URL
  const PRINT_AGENT_DOWNLOAD_URL = "/downloads/MaverickDinePrintAgent-Setup.exe";

  // Port configuration state
  const [portValue, setPortValue] = useState(printService.getPrintAgentPort().toString());
  const [showPortConfig, setShowPortConfig] = useState(false);

  const handlePortSave = () => {
    const port = parseInt(portValue, 10);
    if (port >= 1024 && port <= 65535) {
      printService.setPrintAgentPort(port);
      setShowPortConfig(false);
      onRefresh(); // Re-check connection with new port
    } else {
      alert('Please enter a valid port number (1024-65535)');
    }
  };

  // If Print Agent not connected, show setup instructions
  if (!status.available) {
    return (
      <div className="bg-slate-50 rounded-xl border border-slate-200 p-8">
        <div className="max-w-lg mx-auto text-center">
          {/* Icon */}
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Laptop size={32} className="text-amber-600" />
          </div>

          {/* Title */}
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Print Agent Not Connected</h3>
          <p className="text-sm text-slate-600 mb-6">
            Install the Print Agent on this computer to enable direct thermal printing to your local printers.
          </p>

          {/* Setup Steps */}
          <div className="bg-white rounded-lg border border-slate-200 p-4 mb-6 text-left">
            <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-3">Quick Setup</h4>
            <div className="space-y-2.5">
              {[
                { step: 1, text: "Download the Print Agent" },
                { step: 2, text: "Install and run the application" },
                { step: 3, text: "Print Agent will appear in your system tray" },
                { step: 4, text: "Click 'Check Connection' below to connect" },
              ].map((item) => (
                <div key={item.step} className="flex items-start gap-3">
                  <div className="w-5 h-5 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-medium">
                    {item.step}
                  </div>
                  <span className="text-sm text-slate-700">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Port Configuration */}
          <div className="bg-white rounded-lg border border-slate-200 p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="text-left">
                <p className="text-xs font-medium text-slate-700">Print Agent Port</p>
                <p className="text-[10px] text-slate-500">Change if Print Agent runs on different port</p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={portValue}
                  onChange={(e) => setPortValue(e.target.value)}
                  className="w-20 px-2 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-teal-500 text-center"
                  min="1024"
                  max="65535"
                />
                <button
                  onClick={handlePortSave}
                  className="px-3 py-1.5 bg-teal-600 text-white text-xs font-medium rounded-lg hover:bg-teal-700 transition-colors"
                >
                  Save
                </button>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={PRINT_AGENT_DOWNLOAD_URL}
              download
              className="w-full sm:w-auto px-5 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors flex items-center justify-center gap-2"
            >
              <Download size={16} />
              Download Print Agent
            </a>
            <button
              onClick={onRefresh}
              disabled={status.checking}
              className="w-full sm:w-auto px-5 py-2.5 bg-white border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {status.checking ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <RefreshCw size={16} />
              )}
              {status.checking ? 'Checking...' : 'Check Connection'}
            </button>
          </div>

          {/* Help Text */}
          <p className="mt-4 text-[11px] text-slate-400">
            Connecting to localhost:{portValue} • Windows Defender may request permission on first run
          </p>
        </div>
      </div>
    );
  }

  // Print Agent connected - show printers
  return (
    <div>
      {/* Connection Status Bar */}
      <div className="mb-4 p-3 bg-teal-50 border border-teal-200 rounded-lg flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-teal-600 rounded-lg flex items-center justify-center">
            <Zap size={18} className="text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-teal-900">Print Agent Connected</span>
              <span className="px-1.5 py-0.5 bg-teal-200 text-teal-800 text-[10px] font-semibold rounded">
                v{status.version || "2.0"}
              </span>
              <span className="px-1.5 py-0.5 bg-slate-200 text-slate-600 text-[10px] font-medium rounded">
                :{portValue}
              </span>
            </div>
            <p className="text-xs text-teal-700">{allPrinters.length} printer(s) configured</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Port Config Button */}
          {showPortConfig ? (
            <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-2 py-1">
              <input
                type="number"
                value={portValue}
                onChange={(e) => setPortValue(e.target.value)}
                className="w-16 px-1 py-0.5 text-xs border-0 focus:outline-none text-center"
                min="1024"
                max="65535"
              />
              <button
                onClick={handlePortSave}
                className="p-1 text-teal-600 hover:bg-teal-50 rounded"
                title="Save Port"
              >
                <CheckCircle size={14} />
              </button>
              <button
                onClick={() => {
                  setPortValue(printService.getPrintAgentPort().toString());
                  setShowPortConfig(false);
                }}
                className="p-1 text-slate-400 hover:bg-slate-50 rounded"
                title="Cancel"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowPortConfig(true)}
              className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              title="Change Port"
            >
              <Settings size={18} />
            </button>
          )}
          <button
            onClick={onRefresh}
            className="p-2 text-slate-500 hover:text-teal-600 hover:bg-teal-100 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw size={18} />
          </button>
          <button
            onClick={onAdd}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors flex items-center gap-2"
          >
            <Plus size={16} />
            Add Printer
          </button>
        </div>
      </div>

      {/* Filters */}
      <PrinterFilters
        filterText={filterText}
        setFilterText={setFilterText}
        filterType={filterType}
        setFilterType={setFilterType}
        viewMode={viewMode}
        setViewMode={setViewMode}
      />

      {/* Printers Grid */}
      {printers.length === 0 ? (
        <EmptyState
          icon={Printer}
          title="No local printers configured"
          description={filterText || filterType !== "ALL" ? "Try adjusting your filters" : "Add your first thermal printer to get started"}
          actionLabel="Add Printer"
          onAction={onAdd}
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {printers.map((printer) => (
            <LocalPrinterCard
              key={printer.id}
              printer={printer}
              onEdit={() => onEdit(printer)}
              onDelete={() => {
                if (window.confirm("Remove this printer from Print Agent?")) {
                  onDelete(printer.id);
                }
              }}
              onTest={() => onTest(printer.id)}
              getPrinterTypeConfig={getPrinterTypeConfig}
            />
          ))}
        </div>
      )}

      {/* Results Count */}
      {printers.length > 0 && (
        <div className="mt-4 text-xs text-slate-500">
          Showing {printers.length} of {allPrinters.length} printer(s)
          {(filterText || filterType !== "ALL") && " (filtered)"}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Server Printers Tab Component
// ============================================================
function ServerPrintersTab({
  printers,
  allPrinters,
  kitchenStations,
  loading,
  filterText,
  setFilterText,
  filterType,
  setFilterType,
  viewMode,
  setViewMode,
  onRefresh,
  onAdd,
  onEdit,
  onDelete,
  onActivate,
  onDeactivate,
  onSetDefault,
  getPrinterTypeConfig,
  getConnectionIcon,
}) {
  return (
    <div>
      {/* Info Banner */}
      <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-lg flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-slate-600 rounded-lg flex items-center justify-center">
            <Server size={18} className="text-white" />
          </div>
          <div>
            <span className="text-sm font-medium text-slate-900">Server-Side Printing</span>
            <p className="text-xs text-slate-600">For on-premise deployments with printers connected to the server</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onRefresh}
            className="p-2 text-slate-500 hover:text-teal-600 hover:bg-slate-100 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw size={18} />
          </button>
          <button
            onClick={onAdd}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors flex items-center gap-2"
          >
            <Plus size={16} />
            Add Printer
          </button>
        </div>
      </div>

      {/* Filters */}
      <PrinterFilters
        filterText={filterText}
        setFilterText={setFilterText}
        filterType={filterType}
        setFilterType={setFilterType}
        viewMode={viewMode}
        setViewMode={setViewMode}
      />

      {/* Loading State */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 size={40} className="text-teal-600 animate-spin mb-4" />
          <p className="text-sm text-slate-600">Loading printers...</p>
        </div>
      ) : printers.length === 0 ? (
        <EmptyState
          icon={Server}
          title="No server printers configured"
          description={filterText || filterType !== "ALL" ? "Try adjusting your filters" : "Add a printer to enable server-side printing"}
          actionLabel="Add Printer"
          onAction={onAdd}
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {printers.map((printer) => (
            <ServerPrinterCard
              key={printer.id}
              printer={printer}
              onEdit={() => onEdit(printer)}
              onDelete={() => {
                if (window.confirm("Delete this printer?")) {
                  onDelete(printer.id);
                }
              }}
              onActivate={() => onActivate(printer.id)}
              onDeactivate={() => onDeactivate(printer.id)}
              onSetDefault={() => onSetDefault(printer.id)}
              getPrinterTypeConfig={getPrinterTypeConfig}
              getConnectionIcon={getConnectionIcon}
            />
          ))}
        </div>
      )}

      {/* Results Count */}
      {!loading && printers.length > 0 && (
        <div className="mt-4 text-xs text-slate-500">
          Showing {printers.length} of {allPrinters.length} printer(s)
          {(filterText || filterType !== "ALL") && " (filtered)"}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Shared Filter Component
// ============================================================
function PrinterFilters({ filterText, setFilterText, filterType, setFilterType, viewMode, setViewMode }) {
  return (
    <div className="mb-4 flex flex-col sm:flex-row gap-3">
      {/* Search */}
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
        <input
          type="text"
          placeholder="Search printers..."
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-all"
        />
      </div>

      {/* Type Filter */}
      <div className="flex items-center gap-2">
        <div className="flex items-center bg-white border border-slate-200 rounded-lg p-1">
          {["ALL", "BILL", "KOT"].map((type) => (
            <button
              key={type}
              onClick={() => setFilterType(type)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1.5 ${
                filterType === type
                  ? "bg-teal-600 text-white"
                  : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              {type === "BILL" && <Receipt size={12} />}
              {type === "KOT" && <UtensilsCrossed size={12} />}
              {type === "ALL" ? "All" : type}
            </button>
          ))}
        </div>

        {/* View Toggle */}
        <div className="flex items-center bg-white border border-slate-200 rounded-lg p-1">
          <button
            onClick={() => setViewMode("card")}
            className={`p-2 rounded-md transition-all ${
              viewMode === "card" ? "bg-teal-600 text-white" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <LayoutGrid size={16} />
          </button>
          <button
            onClick={() => setViewMode("list")}
            className={`p-2 rounded-md transition-all ${
              viewMode === "list" ? "bg-teal-600 text-white" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <List size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Empty State Component
// ============================================================
function EmptyState({ icon: Icon, title, description, actionLabel, onAction }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 bg-white rounded-lg border border-slate-200">
      <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mb-4">
        <Icon size={28} className="text-slate-400" />
      </div>
      <h3 className="text-base font-semibold text-slate-900 mb-1">{title}</h3>
      <p className="text-sm text-slate-500 mb-4">{description}</p>
      <button
        onClick={onAction}
        className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors flex items-center gap-2"
      >
        <Plus size={16} />
        {actionLabel}
      </button>
    </div>
  );
}

// ============================================================
// Local Printer Card Component
// ============================================================
function LocalPrinterCard({ printer, onEdit, onDelete, onTest, getPrinterTypeConfig }) {
  const [testing, setTesting] = useState(false);
  const typeConfig = getPrinterTypeConfig(printer.type);
  const TypeIcon = typeConfig.icon;

  const handleTest = async () => {
    setTesting(true);
    try {
      await onTest();
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 hover:border-teal-300 hover:shadow-md transition-all group relative overflow-hidden">
      {/* Top Border */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-teal-500" />

      {/* Local Badge */}
      <div className="absolute top-3 left-3">
        <div className="flex items-center gap-1 px-2 py-0.5 bg-teal-100 text-teal-700 rounded text-[10px] font-semibold">
          <Laptop size={10} />
          LOCAL
        </div>
      </div>

      {/* Default Badge */}
      {printer.isDefault && (
        <div className="absolute top-3 right-3">
          <div className="flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-[10px] font-semibold">
            <Star size={10} fill="currentColor" />
            DEFAULT
          </div>
        </div>
      )}

      {/* Content */}
      <div className="pt-11 pb-4 px-4">
        {/* Printer Icon */}
        <div className={`w-14 h-14 mx-auto rounded-xl flex items-center justify-center mb-3 ${
          printer.type === "BILL" ? "bg-blue-100" : "bg-amber-100"
        }`}>
          <Printer size={28} className={printer.type === "BILL" ? "text-blue-600" : "text-amber-600"} />
        </div>

        {/* Printer Name */}
        <h3 className="text-center font-semibold text-slate-900 mb-1 truncate">{printer.name}</h3>

        {/* Type Badge */}
        <div className="flex justify-center mb-3">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${typeConfig.bg} ${typeConfig.text} border ${typeConfig.border}`}>
            <TypeIcon size={10} />
            {printer.type === "BILL" ? "Bill Printer" : "KOT Printer"}
          </span>
        </div>

        {/* Connection Info */}
        <div className="bg-slate-50 rounded-lg p-2.5 space-y-1.5">
          <div className="flex items-center gap-2 text-xs">
            {printer.connectionType === "WINDOWS" ? (
              <Monitor size={14} className="text-slate-400" />
            ) : printer.connectionType === "NETWORK" ? (
              <Wifi size={14} className="text-slate-400" />
            ) : (
              <Usb size={14} className="text-slate-400" />
            )}
            <span className="text-slate-600 truncate">
              {printer.connectionType === "WINDOWS" ? printer.printerName :
               printer.connectionType === "NETWORK" ? printer.address : "USB Connection"}
            </span>
          </div>
          <div className="flex items-center justify-between text-[10px] text-slate-500">
            <span>{printer.connectionType}</span>
            <span>{printer.paperWidth || "80mm"}</span>
          </div>
          {printer.type === "KOT" && printer.stationName && (
            <div className="flex items-center gap-1.5 text-[10px] pt-1.5 border-t border-slate-200">
              <ChefHat size={12} className="text-purple-500" />
              <span className="text-purple-700 font-medium">{printer.stationName}</span>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="border-t border-slate-100 px-2 py-2 flex items-center gap-1">
        <button
          onClick={handleTest}
          disabled={testing}
          className="flex-1 py-2 text-xs font-medium text-slate-600 hover:text-teal-600 hover:bg-teal-50 rounded-md transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
        >
          {testing ? <Loader2 size={14} className="animate-spin" /> : <TestTube2 size={14} />}
          Test
        </button>
        <button
          onClick={onEdit}
          className="flex-1 py-2 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors flex items-center justify-center gap-1.5"
        >
          <Edit2 size={14} />
          Edit
        </button>
        <button
          onClick={onDelete}
          className="flex-1 py-2 text-xs font-medium text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors flex items-center justify-center gap-1.5"
        >
          <Trash2 size={14} />
          Delete
        </button>
      </div>
    </div>
  );
}

// ============================================================
// Server Printer Card Component
// ============================================================
function ServerPrinterCard({ printer, onEdit, onDelete, onActivate, onDeactivate, onSetDefault, getPrinterTypeConfig, getConnectionIcon }) {
  const typeConfig = getPrinterTypeConfig(printer.printerType);
  const ConnectionIcon = getConnectionIcon(printer.connectionType);
  const TypeIcon = typeConfig.icon;

  return (
    <div className={`bg-white rounded-lg border transition-all group relative overflow-hidden ${
      printer.isActive
        ? "border-slate-200 hover:border-teal-300 hover:shadow-md"
        : "border-slate-100 opacity-60"
    }`}>
      {/* Top Border */}
      <div className={`absolute top-0 left-0 right-0 h-0.5 bg-slate-400 ${!printer.isActive && "bg-slate-300"}`} />

      {/* Server Badge */}
      <div className="absolute top-3 left-3">
        <div className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold ${
          printer.isActive ? "bg-slate-100 text-slate-700" : "bg-slate-50 text-slate-400"
        }`}>
          <Server size={10} />
          SERVER
        </div>
      </div>

      {/* Status & Default Badge */}
      <div className="absolute top-3 right-3 flex items-center gap-1">
        {printer.isDefault && (
          <div className="flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-[10px] font-semibold">
            <Star size={10} fill="currentColor" />
          </div>
        )}
        <div className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold ${
          printer.isActive ? "bg-teal-100 text-teal-700" : "bg-slate-100 text-slate-500"
        }`}>
          <Circle size={6} fill="currentColor" className={printer.isActive ? "animate-pulse" : ""} />
          {printer.isActive ? "Active" : "Inactive"}
        </div>
      </div>

      {/* Content */}
      <div className="pt-11 pb-4 px-4">
        {/* Printer Icon */}
        <div className={`w-14 h-14 mx-auto rounded-xl flex items-center justify-center mb-3 ${
          printer.printerType === "BILL" ? "bg-blue-100" : "bg-amber-100"
        } ${!printer.isActive && "opacity-50"}`}>
          <Printer size={28} className={printer.printerType === "BILL" ? "text-blue-600" : "text-amber-600"} />
        </div>

        {/* Printer Name */}
        <h3 className="text-center font-semibold text-slate-900 mb-1 truncate">{printer.name}</h3>

        {/* Type Badge */}
        <div className="flex justify-center mb-3">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${typeConfig.bg} ${typeConfig.text} border ${typeConfig.border}`}>
            <TypeIcon size={10} />
            {printer.printerType === "BILL" ? "Bill Printer" : "KOT Printer"}
          </span>
        </div>

        {/* Connection Info */}
        <div className="bg-slate-50 rounded-lg p-2.5 space-y-1.5">
          <div className="flex items-center gap-2 text-xs">
            <ConnectionIcon size={14} className="text-slate-400" />
            <span className="text-slate-600 truncate">
              {printer.connectionType === "WINDOWS" && printer.printerName}
              {printer.connectionType === "NETWORK" && `${printer.ipAddress}:${printer.port || 9100}`}
              {printer.connectionType === "USB" && "USB Connection"}
            </span>
          </div>
          <div className="flex items-center justify-between text-[10px] text-slate-500">
            <span>{printer.connectionType}</span>
            <span>{printer.paperWidth}</span>
          </div>
          {printer.printerType === "KOT" && printer.kitchenStationName && (
            <div className="flex items-center gap-1.5 text-[10px] pt-1.5 border-t border-slate-200">
              <ChefHat size={12} className="text-purple-500" />
              <span className="text-purple-700 font-medium">{printer.kitchenStationName}</span>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="border-t border-slate-100 px-2 py-2 flex items-center gap-1">
        <button
          onClick={onEdit}
          className="flex-1 py-2 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors flex items-center justify-center gap-1.5"
        >
          <Edit2 size={14} />
          Edit
        </button>
        <button
          onClick={() => printer.isActive ? onDeactivate() : onActivate()}
          className={`flex-1 py-2 text-xs font-medium rounded-md transition-colors flex items-center justify-center gap-1.5 ${
            printer.isActive
              ? "text-teal-600 hover:bg-teal-50"
              : "text-slate-500 hover:bg-slate-100"
          }`}
        >
          <Power size={14} />
          {printer.isActive ? "Active" : "Inactive"}
        </button>
        <button
          onClick={onDelete}
          className="flex-1 py-2 text-xs font-medium text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors flex items-center justify-center gap-1.5"
        >
          <Trash2 size={14} />
          Delete
        </button>
      </div>
    </div>
  );
}

// ============================================================
// Local Printer Form Component
// ============================================================
function LocalPrinterForm({ item, kitchenStations, onClose, onSave, onDiscover, onGetWindowsPrinters }) {
  const [formData, setFormData] = useState(
    item || {
      name: "",
      type: "BILL",
      connectionType: "WINDOWS",
      address: "",
      printerName: "",
      paperWidth: "80mm",
      autoCut: true,
      kitchenStationId: null,
      stationName: "",
      isDefault: false,
      isActive: true,
    }
  );
  const [discovering, setDiscovering] = useState(false);
  const [discoveredPrinters, setDiscoveredPrinters] = useState([]);
  const [windowsPrinters, setWindowsPrinters] = useState([]);
  const [loadingWindowsPrinters, setLoadingWindowsPrinters] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleDiscover = async () => {
    setDiscovering(true);
    try {
      const printers = await onDiscover();
      setDiscoveredPrinters(printers || []);
    } finally {
      setDiscovering(false);
    }
  };

  const handleFetchWindowsPrinters = async () => {
    setLoadingWindowsPrinters(true);
    try {
      const printers = await onGetWindowsPrinters();
      setWindowsPrinters(printers || []);
    } finally {
      setLoadingWindowsPrinters(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert("Printer name is required");
      return;
    }
    if (formData.connectionType === "NETWORK" && !formData.address?.trim()) {
      alert("IP address is required for network printers");
      return;
    }
    if (formData.connectionType === "WINDOWS" && !formData.printerName?.trim()) {
      alert("Please select a Windows printer");
      return;
    }
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-teal-600 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center">
              <Laptop size={18} className="text-white" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-white">
                {item ? "Edit Local Printer" : "Add Local Printer"}
              </h2>
              <p className="text-xs text-teal-100">Configure printer in Print Agent</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
            <X size={20} className="text-white" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="p-6 space-y-5">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1.5">
                  Printer Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder="e.g., Bill Printer"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1.5">
                  Printer Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => handleChange("type", e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
                >
                  <option value="BILL">BILL - Cashier Receipt</option>
                  <option value="KOT">KOT - Kitchen Order Ticket</option>
                </select>
              </div>
            </div>

            {/* Connection */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1.5">
                  Connection Type
                </label>
                <select
                  value={formData.connectionType}
                  onChange={(e) => handleChange("connectionType", e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
                >
                  <option value="WINDOWS">Windows Printer (Recommended)</option>
                  <option value="NETWORK">Network (IP)</option>
                  <option value="USB">USB Direct</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1.5">
                  Paper Width
                </label>
                <select
                  value={formData.paperWidth}
                  onChange={(e) => handleChange("paperWidth", e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
                >
                  <option value="80mm">80mm (Standard)</option>
                  <option value="58mm">58mm (Compact)</option>
                </select>
              </div>
            </div>

            {/* Windows Printer Selection */}
            {formData.connectionType === "WINDOWS" && (
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1.5">
                  Windows Printer <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formData.printerName}
                    onChange={(e) => handleChange("printerName", e.target.value)}
                    placeholder="Select or type printer name"
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
                  />
                  <button
                    type="button"
                    onClick={handleFetchWindowsPrinters}
                    disabled={loadingWindowsPrinters}
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 flex items-center gap-2 disabled:opacity-50"
                  >
                    {loadingWindowsPrinters ? <Loader2 size={16} className="animate-spin" /> : <ScanSearch size={16} />}
                    Detect
                  </button>
                </div>
                {windowsPrinters.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {windowsPrinters.map((p, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => {
                          handleChange("printerName", p.printerName || p.name);
                          if (!formData.name) handleChange("name", p.printerName || p.name);
                        }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 ${
                          formData.printerName === (p.printerName || p.name)
                            ? "bg-teal-600 text-white"
                            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                        }`}
                      >
                        <Monitor size={12} />
                        {p.printerName || p.name}
                      </button>
                    ))}
                  </div>
                )}
                <p className="text-xs text-slate-500 mt-2">
                  Select your thermal printer from Windows installed printers. Make sure the printer is properly installed with the correct driver.
                </p>
              </div>
            )}

            {/* Network Settings */}
            {formData.connectionType === "NETWORK" && (
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1.5">
                  IP Address & Port <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => handleChange("address", e.target.value)}
                    placeholder="192.168.1.100:9100"
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
                  />
                  <button
                    type="button"
                    onClick={handleDiscover}
                    disabled={discovering}
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 flex items-center gap-2 disabled:opacity-50"
                  >
                    {discovering ? <Loader2 size={16} className="animate-spin" /> : <ScanSearch size={16} />}
                    Discover
                  </button>
                </div>
                {discoveredPrinters.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {discoveredPrinters.map((p, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => handleChange("address", p.address)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 ${
                          formData.address === p.address
                            ? "bg-teal-600 text-white"
                            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                        }`}
                      >
                        <Wifi size={12} />
                        {p.address}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* USB Info */}
            {formData.connectionType === "USB" && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                <AlertCircle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">USB Direct - Not Recommended</p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    USB direct connection has compatibility issues with some printers.
                    Please use <strong>Windows Printer</strong> connection type instead for better reliability.
                  </p>
                </div>
              </div>
            )}

            {/* Kitchen Station (KOT only) */}
            {formData.type === "KOT" && (
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1.5">
                  Kitchen Station
                </label>
                <select
                  value={formData.kitchenStationId || ""}
                  onChange={(e) => {
                    const stationId = e.target.value ? parseInt(e.target.value) : null;
                    handleChange("kitchenStationId", stationId);
                    // Also set stationName for display
                    const station = kitchenStations.find(s => s.id === stationId);
                    handleChange("stationName", station ? station.name : "");
                  }}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
                >
                  <option value="">-- No Station (Default KOT) --</option>
                  {kitchenStations.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">KOTs for this station will be sent to this printer</p>
              </div>
            )}

            {/* Settings */}
            <div className="grid grid-cols-2 gap-4">
              <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100">
                <input
                  type="checkbox"
                  checked={formData.autoCut}
                  onChange={(e) => handleChange("autoCut", e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                />
                <div>
                  <span className="text-sm font-medium text-slate-800">Auto Cut</span>
                  <p className="text-xs text-slate-500">Cut paper after printing</p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100">
                <input
                  type="checkbox"
                  checked={formData.isDefault}
                  onChange={(e) => handleChange("isDefault", e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                />
                <div>
                  <span className="text-sm font-medium text-slate-800">Set as Default</span>
                  <p className="text-xs text-slate-500">Default for {formData.type}</p>
                </div>
              </label>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-slate-50 px-6 py-4 flex items-center justify-end gap-3 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 flex items-center gap-2 disabled:opacity-50"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              {item ? "Update" : "Add"} Printer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================
// Server Printer Form Component
// ============================================================
function ServerPrinterForm({ item, kitchenStations, systemPrinters, loadingSystemPrinters, onFetchSystemPrinters, onClose, onSave }) {
  const [formData, setFormData] = useState(
    item || {
      name: "",
      description: "",
      printerType: "BILL",
      connectionType: "WINDOWS",
      printerName: "",
      ipAddress: "",
      port: 9100,
      paperWidth: "80mm",
      kitchenStationId: null,
      isDefault: false,
      isActive: true,
    }
  );
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert("Printer name is required");
      return;
    }
    if (formData.connectionType === "WINDOWS" && !formData.printerName?.trim()) {
      alert("Windows printer name is required");
      return;
    }
    if (formData.connectionType === "NETWORK" && !formData.ipAddress?.trim()) {
      alert("IP address is required");
      return;
    }
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-teal-600 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center">
              <Server size={18} className="text-white" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-white">
                {item ? "Edit Server Printer" : "Add Server Printer"}
              </h2>
              <p className="text-xs text-teal-100">For on-premise deployments</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
            <X size={20} className="text-white" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="p-6 space-y-5">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1.5">
                  Printer Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder="e.g., Bill Printer"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1.5">
                  Printer Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.printerType}
                  onChange={(e) => handleChange("printerType", e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
                >
                  <option value="BILL">BILL - Cashier Receipt</option>
                  <option value="KOT">KOT - Kitchen Order Ticket</option>
                </select>
              </div>
            </div>

            {/* Connection */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1.5">
                  Connection Type
                </label>
                <select
                  value={formData.connectionType}
                  onChange={(e) => handleChange("connectionType", e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
                >
                  <option value="WINDOWS">Windows Printer</option>
                  <option value="NETWORK">Network (IP)</option>
                  <option value="USB">USB Direct</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1.5">
                  Paper Width
                </label>
                <select
                  value={formData.paperWidth}
                  onChange={(e) => handleChange("paperWidth", e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
                >
                  <option value="80mm">80mm (Standard)</option>
                  <option value="58mm">58mm (Compact)</option>
                </select>
              </div>
            </div>

            {/* Windows Printer */}
            {formData.connectionType === "WINDOWS" && (
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1.5">
                  Windows Printer <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formData.printerName}
                    onChange={(e) => handleChange("printerName", e.target.value)}
                    placeholder="Select or type printer name"
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
                  />
                  <button
                    type="button"
                    onClick={onFetchSystemPrinters}
                    disabled={loadingSystemPrinters}
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 flex items-center gap-2 disabled:opacity-50"
                  >
                    {loadingSystemPrinters ? <Loader2 size={16} className="animate-spin" /> : <ScanSearch size={16} />}
                    Detect
                  </button>
                </div>
                {systemPrinters.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {systemPrinters.map((name, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => {
                          handleChange("printerName", name);
                          if (!formData.name) handleChange("name", name);
                        }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 ${
                          formData.printerName === name
                            ? "bg-teal-600 text-white"
                            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                        }`}
                      >
                        <Printer size={12} />
                        {name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Network Settings */}
            {formData.connectionType === "NETWORK" && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1.5">
                    IP Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.ipAddress}
                    onChange={(e) => handleChange("ipAddress", e.target.value)}
                    placeholder="192.168.1.100"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1.5">Port</label>
                  <input
                    type="number"
                    value={formData.port}
                    onChange={(e) => handleChange("port", parseInt(e.target.value) || 9100)}
                    placeholder="9100"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
                  />
                </div>
              </div>
            )}

            {/* Kitchen Station (KOT only) */}
            {formData.printerType === "KOT" && (
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1.5">
                  Kitchen Station
                </label>
                <select
                  value={formData.kitchenStationId || ""}
                  onChange={(e) => handleChange("kitchenStationId", e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
                >
                  <option value="">-- No Station (Default KOT) --</option>
                  {kitchenStations.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Settings */}
            <div className="grid grid-cols-2 gap-4">
              <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => handleChange("isActive", e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                />
                <div>
                  <span className="text-sm font-medium text-slate-800">Active</span>
                  <p className="text-xs text-slate-500">Enable this printer</p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100">
                <input
                  type="checkbox"
                  checked={formData.isDefault}
                  onChange={(e) => handleChange("isDefault", e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                />
                <div>
                  <span className="text-sm font-medium text-slate-800">Set as Default</span>
                  <p className="text-xs text-slate-500">Default for {formData.printerType}</p>
                </div>
              </label>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-slate-50 px-6 py-4 flex items-center justify-end gap-3 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 flex items-center gap-2 disabled:opacity-50"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              {item ? "Update" : "Add"} Printer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
